import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import './Login.css';

const Login = () => {
  // state.loginIdを定義(初期値は'')
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState(''); 
  // パスワード表示/非表示の切り替え状態
  const [showPassword, setShowPassword] = useState(false);
  // クライアントサイドのバリデーションメッセージ用
  const [passwordError, setPasswordError] = useState<string | null>(null);
  // ログインエラー用
  const [loginError, setLoginError] = useState<string | null>(null);

  // AuthProviderが提供しているAuthContextインスタンスを利用するための関数がuseAuth()関数
  // 　※AuthProviderはAuthContext.tsx内に定義れている
  // 　※useAuth()関数はAuthContext.tsxで定義されており、AuthContextType型のインスタンスを返す
  // 　※AuthContextType型はAuthContext.tsx内にAuthContextTypeというinterfaceで定義されている
  // 　※AuthContextインスタンスの実態はAuthProvider内で実装されている
  // 　※useAuth()関数を利用するには下記のようにAuthProviderタグで囲まれている下位層である必要がある（下記はApp.tsx）
  // 　<AuthProvider>
  //  　<BidsProvider>
  //    　<ItemsProvider>
  //     　<AppRouter />
  //    　</ItemsProvider>
  //  　</BidsProvider>
  // 　</AuthProvider>
  // useAuth()が返すオブジェクトの中からloginという名前のプロパティ（この場合は関数）を取り出して、loginという名前の定数としてこの場で使えるようにする
  // 　※AuthProviderが提供している値は { currentUser, login, logout } というオブジェクトなので、useAuth()を呼び出すとこのオブジェクトが返ってきます。そこから必要なものだけを{}で囲って取り出している
  const { login, isNewPasswordRequired, completeNewPassword } = useAuth();

  // loginボタンsubmit時の関数
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError(null);
    await new Promise(resolve => setTimeout(resolve, 50)); // (ログイン中...への切替) 

    try {
      await login(loginId, password);
    } catch (error) {
      console.error('Login failed:', error);
      const message = error instanceof Error ? error.message : 'ログインに失敗しました。';
      setLoginError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // パスワードの強度チェック
  const validatePassword = (pwd: string) => {

    // 1. 半角英数字・記号以外が含まれていないかチェック
    // [^\x21-\x7e] は、半角記号・英数字以外の文字（全角文字など）にマッチします
    if (/[^\x21-\x7e]/.test(pwd)) {
      return 'パスワードは半角英数字・記号で入力してください。';
    }

    // 2. 文字数チェック
    if (pwd.length < 10) {
      return '10文字以上に設定してください。';
    }
    if (pwd.length > 128) {
      return '128文字以内で設定してください。';
    }
    
    // 3. 文字種チェック（大文字、小文字、数字、記号）
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasMark = /[!@#$%^&*()_+={}\[\]|\\:;"'<>,.?\/~`-]/.test(pwd);//32種を記号として判定
    
    const typeCount = [hasUpper, hasLower, hasNumber, hasMark].filter(Boolean).length;
    
    if (typeCount < 3) {
      return '文字の種類を3種類以上設定してください。';
    }
    
    return null;
  };

  // 新しいパスワード設定処理
  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // クライアント側バリデーション
    const error = validatePassword(newPassword);
    if (error) {
      setPasswordError(error);
      return;
    }
    
    setPasswordError(null);
    setIsLoading(true);
    try {
      await completeNewPassword(newPassword);
      // 成功するとHubが検知し、AuthHandlerが画面遷移を行う
    } catch (error) {
      console.error('New password submission failed:', error);
      toast.error(error instanceof Error ? error.message : 'パスワードの設定に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  // isNewPasswordRequired の状態に応じて表示するフォームを切り替え
  if (isNewPasswordRequired) {
    return (
      <div className="flex flex-col items-center justify-start min-h-[80vh] p-8 px-4" >
        <div className="flex justify-center mb-4 w-full">
          <h1 className="font-bold text-2xl text-emerald-900 border-b-4 border-double border-emerald-500 pb-2 px-4 block w-fit text-center leading-relaxed" >
            新しいパスワードを設定
          </h1>
        </div>
        
        <form onSubmit={handleNewPasswordSubmit} className="w-full max-w-[400px] mx-auto">
        <div className="flex flex-col text-left">
          <label htmlFor="newPassword" className="block !mb-2 font-bold">
              新しいパスワード
            </label>
            <div className="flex items-center gap-4">
              <input
                id="newPassword"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  // 半角記号・英数字以外（全角文字など）を即座に削除
                  const sanitizedValue = e.target.value.replace(/[^\x21-\x7e]/g, '');
                  setNewPassword(sanitizedValue);
                  
                  if (passwordError) setPasswordError(null); 
                }}
                required
                disabled={isLoading}
                className="w-full max-w-[300px] p-2 border border-gray-300 rounded outline-none focus:border-emerald-500"
              />
                <label className="flex items-center gap-1 cursor-pointer whitespace-nowrap select-none m-0">
                <input
                  type="checkbox"
                  className="w- cursor-pointer align-middle"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
                <span className="leading-none inline-block">パスワードを表示</span>
              </label>
            </div>
          </div>

          <button 
            type="submit" 
            className="w-full p-[0.8rem] bg-blue-600 hover:bg-blue-700 text-white rounded font-bold mt-4 disabled:bg-gray-400" 
            disabled={isLoading}
          >
            {isLoading ? '設定中...' : 'パスワードを設定'}
          </button>

          {/* ボタンとパスワード設定条件の間にエラーメッセージ領域を追加 */}
          <div className="min-h-[1.5rem] text-left my-2">
            {passwordError && (
              <p className="text-red-600 font-bold text-m">
                {passwordError}
              </p>
            )}
          </div>
        </form>

        {/* 手順説明：パスワード設定条件の部分 */}
        <div className="bg-slate-50 p-3 my-2 rounded border border-slate-200 text-left max-w-[400px] mx-auto">
          <p className="font-bold">＜パスワード設定条件＞</p>
          <div className="mt-2 space-y-2">
            {/* 1行目 */}
            <div className="flex">
              <span className="shrink-0 mr-2">・</span>
              <span>文字数：10文字以上、128文字以内</span>
            </div>
            {/* 2行目 */}
            <div className="flex">
              <span className="shrink-0 mr-2">・</span>
              <div className="flex flex-wrap">
                <span className="shrink-0">文字種：</span>
                <div className="flex-1">
                  半角のアルファベット大文字、小文字、数字、記号の内から
                  <span className="font-bold text-red-600 underline">3種類以上</span>
                  を使用
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="text-left max-w-[400px] mx-auto my-4">
          <p className="mt-4 text-m">※変更後のパスワードはご自身で厳重に管理してください。</p>
          <p className="text-m">※パスワードを紛失した場合は、静岡茶市場までお問い合わせ<br className="hidden sm:inline" />ください。</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="login-container">
      <h1 className="inline-block text-2xl lg:text-4xl font-bold text-emerald-900 font-serif border-b-4 border-double border-emerald-500 pb-2 px-4 text-center"><span className="inline-block">静岡茶市場</span><span className="inline-block">電子入札システム</span></h1>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="loginId">ログインID</label>
          <input
            id="loginId"
            type="text"
            // state.loginIdを表示値としてセット
            value={loginId}
            // onChangeイベントが発火したらset関数経由でstate.loginIdの内容を入力値でreactに更新通知する
            // reactに更新通知されると再レンダリングされて表示内容が更新される
            onChange={(e) => setLoginId(e.target.value)}
            required
            disabled={isLoading} // ローディング中は無効化
          />
        </div>
        <div className="flex-col text-left">
          <label htmlFor="password" className="font-bold">パスワード</label>
          <div className="items-center">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading} // ローディング中は無効化
            className="w-full p-2 border border-gray-300 rounded outline-none focus:border-emerald-500"
          />
          <label className="flex items-center gap-1 cursor-pointer whitespace-nowrap select-none m-2">
                <input
                  type="checkbox"
                  className="w- cursor-pointer align-middle"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
                <span className="leading-none inline-block">パスワードを表示</span>
              </label>
        </div>
        </div>
        {/* submitボタンを定義 （押下すると上位formのonSubmitイベントが発火する）*/}
        <button type="submit" className="login-button">
          {isLoading ? 'ログイン中...' : 'ログイン'}
        </button>

        {/* ログイン失敗時のメッセージ表示領域 */}
        <div className="min-h-[1.5rem] text-left mt-4">
          {loginError && (
            <p className="whitespace-pre-wrap text-red-600 font-bold text-m p-2">
              {loginError}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default Login;